import { AppBar, Box, IconButton, Stack, Toolbar, Tooltip } from '@material-ui/core'
// material
import { alpha, styled } from '@material-ui/core/styles'
import { Link as RouterLink, useLocation } from 'react-router-dom'
// components
import Logo from 'src/components/Logo'
import { PATH_DASHBOARD } from '../../routes/paths'
import AccountPopover from './AccountPopover'
import { useOrganisationDetails } from '../../api/user/list'
import { useManageUser } from 'src/api/user/manage'
import useAuth from '../../hooks/useAuth'
import NotificationPopover from './NotificationPopover'
import StatsSummary from './StatsSummary'
import { LANDING_PAGE_URL } from '../../constants'
import UploadTracker from 'src/components/upload/UploadTracker'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import useChatbotStore from 'src/components/chatbot/stores/chatbotStore'
import useChat from 'src/components/chatbot/hooks/useChat'

// ----------------------------------------------------------------------

// Views offset their content against this height (layouts/dashboard/index.js,
// calc(100vh - Npx) in several views), so it must not change.
const APPBAR_HEIGHT = 64

const RootStyle = styled(AppBar)(({ theme }) => ({
  width: '100%',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  backgroundImage: 'none',
  // Hairline drawn inside the bar so the bar stays exactly APPBAR_HEIGHT tall.
  boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
}))

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_HEIGHT,
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0, 3),
  },
}))

const NavTab = styled(RouterLink, { shouldForwardProp: (prop) => prop !== 'active' })(({ theme, active }) => ({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  height: APPBAR_HEIGHT,
  padding: theme.spacing(0, 1.5),
  ...theme.typography.body2,
  fontWeight: theme.typography.fontWeightMedium,
  whiteSpace: 'nowrap',
  textDecoration: 'none',
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  transition: theme.transitions.create('color', { duration: theme.transitions.duration.shorter }),
  '&:hover': {
    color: theme.palette.text.primary,
  },
  '&:focus-visible': {
    outlineOffset: -2,
  },
  '&::after': {
    content: "''",
    position: 'absolute',
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    bottom: 0,
    height: 2,
    borderRadius: '2px 2px 0 0',
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
  },
}))

// Shared by every icon action on the right of the bar (Chat AI, uploads, notifications).
const NAVBAR_ICON_BUTTON_SX = {
  width: 36,
  height: 36,
  p: 0,
  borderRadius: 1,
  color: 'action.active',
  '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
  '& .MuiSvgIcon-root': { fontSize: 20 },
}

// Result screens are opened from the analysis table on Home, so Home stays
// highlighted on them and the user always knows where they are.
const HOME_ROUTES = [
  PATH_DASHBOARD.general.home,
  PATH_DASHBOARD.general.batches,
  '/libra/variants',
  '/libra/sample',
  '/libra/cnv',
  '/libra/somatic-variants',
  '/libra/methylation',
  '/libra/cohort',
  '/libra/carrier-report',
]

const NAV_ITEMS = [
  { label: 'Home', to: PATH_DASHBOARD.general.home, match: HOME_ROUTES },
  { label: 'Files', to: PATH_DASHBOARD.general.files, match: [PATH_DASHBOARD.general.files] },
  // { label: 'Kits/Panels', to: PATH_DASHBOARD.general.kits, match: [PATH_DASHBOARD.general.kits] },
  // { label: 'Reports', to: PATH_DASHBOARD.general.reports, match: [PATH_DASHBOARD.general.reports] },
  // { label: 'Search', to: PATH_DASHBOARD.general.search, match: [PATH_DASHBOARD.general.search] },
  { label: 'Admin', to: PATH_DASHBOARD.general.admin, match: [PATH_DASHBOARD.general.admin], superuserOnly: true },
  // Official GİB e-documents. Sits beside Admin because it shares its
  // is_superuser gate, but is its own entry: reviewing invoices is a
  // recurring daily task, not something to go hunting for inside a
  // system-status screen.
  {
    label: 'Faturalar',
    to: PATH_DASHBOARD.general.ebelge,
    match: [PATH_DASHBOARD.general.ebelge],
    superuserOnly: true,
  },
]

const matchesRoute = (pathname, prefixes) =>
  prefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))

// ----------------------------------------------------------------------

export default function DashboardNavbar() {
  const { pathname } = useLocation()
  var organisationDetails = useOrganisationDetails()
  organisationDetails = organisationDetails.data || {}
  const { user } = useAuth()
  const { self } = useManageUser(user)
  const userDetails = self.data

  const fullName = [userDetails?.first_name, userDetails?.last_name].filter(Boolean).join(' ')

  // Chat button state
  const { setShowChat, scrollToBottom } = useChatbotStore()
  const { setVisibility } = useChat()

  const handleChatClick = () => {
    setShowChat(true)
    setVisibility(true)
    setTimeout(() => {
      scrollToBottom()
    }, 100)
  }

  const navItems = NAV_ITEMS.filter((item) => !item.superuserOnly || user?.is_superuser)

  return (
    <RootStyle>
      <ToolbarStyle>
        <Box
          component="a"
          href={LANDING_PAGE_URL}
          aria-label="Lidya Genomics"
          sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, textDecoration: 'none', borderRadius: 1 }}
        >
          <Logo mark sx={{ display: { xs: 'block', sm: 'none' } }} />
          <Logo sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }} />
        </Box>

        <Box
          component="nav"
          aria-label="Main"
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            ml: { xs: 1, sm: 3 },
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {navItems.map((item) => {
            const active = matchesRoute(pathname, item.match)
            return (
              <NavTab key={item.label} to={item.to} active={active} aria-current={active ? 'page' : undefined}>
                {item.label}
              </NavTab>
            )
          })}
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }} sx={{ flexShrink: 0 }}>
          <StatsSummary />

          <Tooltip title="Chat AI">
            <IconButton
              onClick={handleChatClick}
              aria-label="Open Chat AI"
              sx={{
                ...NAVBAR_ICON_BUTTON_SX,
                position: 'relative',
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            >
              <Box
                component="img"
                src="/static/brand/minimal_logo.png"
                alt=""
                sx={{ width: 20, height: 20, objectFit: 'contain' }}
              />
              <AutoAwesomeIcon
                sx={{
                  position: 'absolute',
                  top: 3,
                  right: 3,
                  width: 12,
                  height: 12,
                  color: 'primary.main',
                }}
              />
            </IconButton>
          </Tooltip>

          {/* UploadTracker owns its tooltip and state colours; only its size is aligned here. */}
          <Box
            sx={{
              display: 'inline-flex',
              '& .MuiIconButton-root': {
                width: 36,
                height: 36,
                p: 0,
                borderRadius: 1,
              },
              '& .MuiIconButton-root > .MuiBadge-root > .MuiSvgIcon-root': { fontSize: 20 },
            }}
          >
            <UploadTracker />
          </Box>

          <NotificationPopover />

          <AccountPopover name={fullName} organisation={organisationDetails.name} email={userDetails?.email} />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  )
}
